import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  AdCampaignModuleType,
  SampleRequest_RestaurantAddressQuery,
  SampleRequest_RestaurantAddressQueryVariables,
  SampleRequest_SubmitMutation,
  SampleRequest_SubmitMutationVariables,
} from '@/types';
import {
  ADDRESS_FIELDS_FRAGMENT,
  AddressInput,
  getAddressInputFromPlaceResult,
} from '@/components/addresses/address-input';
import { AddressInputType } from '@domain/addresses/types/types';
import { array, object, string } from 'yup';
import { Button } from '@/components/button';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { EmailInput } from '@/components/email-input';
import { formatAddressForInput } from '@/utils/address';
import { FormInputContainer } from '@/components/form';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { PlacesSearchComponent } from '@/components/locations/places-search-component';
import {
  SAMPLE_REQUEST_CONTEXT_QUERY,
  SAMPLE_REQUEST_FRAGMENT,
} from '@/contexts/ads-context-queries';
import { SampleRequestLimitMessage } from './sample-request-limit-message';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { ScrollView, Text, useWindowDimensions, View } from 'react-native';
import { TextInput } from '@/components/text-input';
import { ToastContext, useToasts } from '@/contexts/toast-context';
import { Type } from '@/components/toast';
import { useAdsContext } from '@/contexts/ads-context';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useFormik } from 'formik';
import { useLayoutContext } from '@/contexts/layout-context';

export enum SampleRequestActionSheetRoute {
  Address = 'ADDRESS',
  Submit = 'SUBMIT',
}

export const SAMPLE_REQUEST_ADDRESS_QUERY = gql`
  query SampleRequest_RestaurantAddress($restaurantUUID: uuid!) {
    businesses_by_pk(uuid: $restaurantUUID) {
      address {
        ...AddressInputFields
      }
      uuid
    }
  }

  ${ADDRESS_FIELDS_FRAGMENT}
`;

export const SUBMIT_SAMPLE_REQUEST_MUTATION = gql`
  mutation SampleRequest_Submit(
    $adCampaignUUID: uuid!
    $contactInfo: RequestContactInfo
    $formSubmitEventPayload: AdEventInput!
    $qualificationResponses: [QualificationResponse!]
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
  ) {
    requestASample(
      ad_campaign_id: $adCampaignUUID
      contact_info: $contactInfo
      form_submit_event_payload: $formSubmitEventPayload
      qualification_responses: $qualificationResponses
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_id: $variantPackUUID
    ) {
      sample_request {
        ...SampleRequestFields
      }
    }
  }

  ${SAMPLE_REQUEST_FRAGMENT}
`;

interface Props {
  onClose: () => void;
  sampleRequestSku: ItemListOptionMetadata;
}

export const SampleRequestConfirmationActionSheet: React.FC<Props> = ({
  onClose,
  sampleRequestSku,
}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
    user,
  } = useAuthenticatedUserContext();
  const { latestSampleRequest } = useAdsContext();
  const { showToast } = useToasts();
  const [styles] = useStyles();
  const windowDimensions = useWindowDimensions();
  const { isSmallLayout } = useLayoutContext();

  const [address, setAddress] = useState<AddressInputType | null>(
    latestSampleRequest?.address
      ? formatAddressForInput(latestSampleRequest.address)
      : null,
  );
  const [address2, setAddress2] = useState(
    latestSampleRequest?.address?.address2 ?? '',
  );
  const [addressUUID, setAddressUUID] = useState(
    latestSampleRequest?.address?.uuid,
  );

  const sampleRequest = sampleRequestSku.sampleRequest!;
  const requireContactInfo = sampleRequest.require_contact_information;
  const qualificationQuestions = sampleRequest.qualification_questions;
  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.9);

  const [dynamicActionSheetMaxHeight, setDynamicActionSheetMaxHeight] =
    useState(actionSheetMaxHeight);

  const { data: restaurantAddressData, loading: restaurantAddressLoading } =
    useQuery<
      SampleRequest_RestaurantAddressQuery,
      SampleRequest_RestaurantAddressQueryVariables
    >(SAMPLE_REQUEST_ADDRESS_QUERY, {
      // Only query for restaurant address if previous
      // sample request did not include an address
      skip: Boolean(address),
      variables: {
        restaurantUUID,
      },
    });

  useEffect(() => {
    const restaurantAddress = restaurantAddressData?.businesses_by_pk?.address;
    if (restaurantAddress) {
      setAddress(formatAddressForInput(restaurantAddress));
      setAddress2(restaurantAddress.address2 ?? '');
      setAddressUUID(restaurantAddress.uuid);
    }
  }, [restaurantAddressData]);

  const [submitSampleRequest, { loading }] = useMutation<
    SampleRequest_SubmitMutation,
    SampleRequest_SubmitMutationVariables
  >(SUBMIT_SAMPLE_REQUEST_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(SAMPLE_REQUEST_CONTEXT_QUERY) as string],
  });

  const addressRouteStyle = [
    styles.flex,
    { maxHeight: dynamicActionSheetMaxHeight },
  ];
  const [addressInputText, emailInputText] = requireContactInfo
    ? [`${i18n.t('business_address')}*`, `${i18n.t('email')}*`]
    : [i18n.t('business_address'), i18n.t('email')];

  const sampleRequestForm = useFormik({
    initialValues: {
      email:
        latestSampleRequest?.contact_email ?? user.contact_info.email ?? '',
      qualificationResponses: qualificationQuestions
        ? qualificationQuestions.map(() => '')
        : undefined,
    },
    onSubmit: async (values) => {
      const response = await submitSampleRequest({
        variables: {
          adCampaignUUID: sampleRequest.ad_campaign_id,
          contactInfo: {
            ...(addressUUID
              ? { address_id: addressUUID }
              : {
                  address: address?.address,
                  address2: address2.trim() || undefined,
                }),
            contact_email: values.email.trim() || undefined,
          },
          formSubmitEventPayload: {
            ad_campaign_id: sampleRequest.ad_campaign_id,
            ad_manager_id: sampleRequest.ad_manager_id,
            ad_module: AdCampaignModuleType.SampleRequest,
            advertiser_id: sampleRequest.advertiser_id,
            event_id:
              sampleRequest.sample_request_event_data.form_submit_event_id,
            gtin: sampleRequest.gtin,
          },
          qualificationResponses:
            qualificationQuestions && values.qualificationResponses
              ? values.qualificationResponses.map((res, index) => ({
                  question: qualificationQuestions[index],
                  response: res,
                }))
              : undefined,
          restaurantUUID,
          supplierUUID,
          variantPackUUID: sampleRequestSku.variantPackUUID,
        },
      });

      if (response.data?.requestASample.sample_request) {
        showToast({
          content: i18n.t('toast.sample_request.success'),
          type: Type.POSITIVE,
        });
        onClose();
      } else {
        throw new Error('Unable to submit sample request');
      }
    },
    validationSchema: object({
      email: requireContactInfo
        ? string()
            .email(i18n.t('toast.invalid_email'))
            .required(i18n.t('field_is_required'))
        : string().email(i18n.t('toast.invalid_email')),
      qualificationResponses: array().of(
        string().required(i18n.t('field_is_required')),
      ),
    }),
  });

  const { errors, handleChange, submitForm, touched, values } =
    sampleRequestForm;

  return (
    <CombinedActionSheet
      animateTransitions
      initialRoute={SampleRequestActionSheetRoute.Submit}
      onClose={onClose}
      onSwipeCancel={() => setDynamicActionSheetMaxHeight(actionSheetMaxHeight)}
      onSwipeMove={(percentageShown) => {
        setDynamicActionSheetMaxHeight(
          Math.floor(actionSheetMaxHeight * percentageShown),
        );
      }}
      propagateSwipe
      renderScene={({ routeKey, jumpTo }) => {
        switch (routeKey) {
          case SampleRequestActionSheetRoute.Address:
            return (
              <View style={[styles.flex, styles.placesContainer]}>
                <PlacesSearchComponent
                  initialValue={address?.address}
                  onSelect={(_, placeResult) => {
                    if (placeResult) {
                      setAddress(
                        getAddressInputFromPlaceResult(placeResult, address2),
                      );
                      // If a new address is selected, create a new address entry
                      setAddressUUID(undefined);
                    }

                    jumpTo(SampleRequestActionSheetRoute.Submit);
                  }}
                  placeholder={i18n.t('business_address')}
                />
              </View>
            );
          case SampleRequestActionSheetRoute.Submit:
            return (
              // Render error toasts within BaseActionSheet's ToastContextProvider
              // but success toasts outside of action sheet due to closing on submit
              <ToastContext.Consumer>
                {(context) => (
                  <ScrollView>
                    <View style={styles.messageWrapper}>
                      <Text style={[TYPOGRAPHY.p]}>
                        {i18n.t('sample_request.message')}
                      </Text>
                    </View>
                    <SampleRequestLimitMessage sampleRequest={sampleRequest} />
                    <FormInputContainer>
                      {restaurantAddressLoading ? (
                        <ActivityIndicator />
                      ) : (
                        <>
                          <AddressInput
                            address2={address2}
                            address2Props={{
                              containerStyle: [
                                styles.inputBorder,
                                styles.messageWrapper,
                              ],
                            }}
                            addressProps={{
                              containerStyle: styles.messageWrapper,
                              inputContainerStyle: styles.inputBorder,
                              placeholder: addressInputText,
                            }}
                            label={address ? addressInputText : undefined}
                            onAddress2Change={setAddress2}
                            onOpenSearch={() =>
                              jumpTo(SampleRequestActionSheetRoute.Address)
                            }
                            useOuterLabel
                            value={address}
                          />
                          <View style={styles.messageWrapper}>
                            <EmailInput
                              containerStyle={styles.inputBorder}
                              error={
                                touched.email && errors.email
                                  ? errors.email
                                  : undefined
                              }
                              label={emailInputText}
                              onChangeText={handleChange('email')}
                              placeholder={
                                requireContactInfo
                                  ? emailInputText
                                  : i18n.t('email_optional')
                              }
                              useOuterLabel
                              value={values.email}
                            />
                          </View>
                          {qualificationQuestions
                            ? qualificationQuestions.map((question, index) => (
                                <View
                                  key={question}
                                  style={styles.messageWrapper}
                                >
                                  <TextInput
                                    containerStyle={styles.inputBorder}
                                    error={
                                      touched.qualificationResponses &&
                                      errors.qualificationResponses
                                        ? errors.qualificationResponses[index]
                                        : undefined
                                    }
                                    label={question}
                                    onChangeText={handleChange(
                                      `qualificationResponses[${index}]`,
                                    )}
                                    placeholder={`${i18n.t('response')}*`}
                                    useOuterLabel
                                    value={
                                      values.qualificationResponses
                                        ? values.qualificationResponses[index]
                                        : ''
                                    }
                                  />
                                </View>
                              ))
                            : null}
                        </>
                      )}
                    </FormInputContainer>
                    <Button
                      disabled={requireContactInfo ? !address : false}
                      loading={loading}
                      onPress={async () => {
                        try {
                          await submitForm();
                        } catch {
                          context.showErrorToast(
                            i18n.t('toast.sample_request.failure'),
                          );
                        }
                      }}
                      trackingData={{
                        name: 'sample_request.submit',
                      }}
                    >
                      {i18n.t('submit')}
                    </Button>
                  </ScrollView>
                )}
              </ToastContext.Consumer>
            );
        }
      }}
      routes={{
        [SampleRequestActionSheetRoute.Address]: {
          contentStyle: addressRouteStyle,
          modalStyle: addressRouteStyle,
          previousKey: SampleRequestActionSheetRoute.Submit,
          showSmallLayoutBackButton: true,
          smallLayoutBackButtonStyle: styles.backButton,
        },
        [SampleRequestActionSheetRoute.Submit]: {
          contentStyle: addressRouteStyle,
          modalStyle: addressRouteStyle,
          previousKey: null,
          showSmallLayoutTitle: true,
          title: i18n.t('sample_request.title', {
            name: sampleRequestSku.displayName,
          }),
        },
      }}
      swipeDirection={isSmallLayout ? 'down' : undefined}
      titleStyle={styles.title}
    />
  );
};

const useStyles = createStyles((theme) => ({
  backButton: {
    marginLeft: 0,
  },
  flex: {
    flex: 1,
  },
  inputBorder: {
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 1,
  },
  messageWrapper: {
    marginBottom: 16,
  },
  placesContainer: {
    marginHorizontal: -SCREEN_PADDING,
  },
  title: {
    marginBottom: 8,
  },
}));
